'use client';

import { useState } from 'react';
import Sidebar from '@/components/Sidebar';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import Footer from '@/components/Footer';
import { Menu, X, Scale, Moon, CloudSnow } from 'lucide-react';
import Link from 'next/link';
import { Inter } from 'next/font/google';
import Breadcrumbs from '@/components/Breadcrumbs';
import { usePathname } from 'next/navigation';

const inter = Inter({ subsets: ['latin'] });

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const pathname = usePathname() || '';

  const isSleepCalc = pathname.startsWith('/sleep-calculator');
  const isSnowCalc = pathname.startsWith('/snow-day-calculator');
  const isUnitConverter = !isSleepCalc && !isSnowCalc;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const navItems = [
    {
      name: 'Unit Converter',
      shortName: 'Units',
      href: '/',
      active: isUnitConverter,
      icon: Scale,
      activeColor: isSleepCalc || isSnowCalc
        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-bold'
        : 'bg-emerald-600 text-white shadow shadow-emerald-200 font-semibold',
    },
    {
      name: 'Sleep Calculator',
      shortName: 'Sleep',
      href: '/sleep-calculator/',
      active: isSleepCalc,
      icon: Moon,
      activeColor: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20 font-bold',
    },
    {
      name: 'Snow Day Calc',
      shortName: 'Snow Day',
      href: '/snow-day-calculator/',
      active: isSnowCalc,
      icon: CloudSnow,
      activeColor: 'bg-sky-500/10 text-sky-400 border-sky-500/20 font-bold',
    },
  ];

  const switcherElement = (
    <nav className={`flex items-center gap-1.5 p-1 rounded-2xl border ${
      isSleepCalc ? 'bg-slate-900 border-slate-800/80' : isSnowCalc ? 'bg-slate-950 border-slate-850/80' : 'bg-gray-100 border-gray-200'
    }`}>
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.name}
            href={item.href}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-transparent transition-all ${
              item.active
                ? item.activeColor
                : `${isSleepCalc || isSnowCalc ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-850/50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{item.name}</span>
            <span className="sm:hidden">{item.shortName}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <html lang="en">
      <head>
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <GoogleAnalytics />
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8520506164876944"
          crossOrigin="anonymous"
        ></script>
      </head>
      <body className={inter.className}>
        <div className={`min-h-screen ${
          isSleepCalc ? 'bg-slate-950' : isSnowCalc ? 'bg-slate-900' : 'bg-gray-50'
        } overflow-x-hidden transition-colors duration-300`}>

          {/* Desktop Top Header */}
          <header className={`hidden desktop:flex fixed top-0 right-0 ${
            isUnitConverter ? 'left-80' : 'left-0'
          } h-16 border-b z-30 items-center justify-between px-6 transition-all duration-300 ${
            isSleepCalc ? 'bg-slate-950/95 border-slate-900 backdrop-blur' : isSnowCalc ? 'bg-slate-900/95 border-slate-800 backdrop-blur' : 'bg-white/95 border-gray-200/80 backdrop-blur'
          }`}>
            <div className="flex-1 flex items-center">
              {!isUnitConverter && (
                <Link href="/" className="flex items-center gap-2 text-slate-200 hover:text-emerald-500 transition-colors">
                  <div className="bg-emerald-600 p-1.5 rounded-lg text-white">
                    <Scale className="w-4 h-4" />
                  </div>
                  <span className="font-extrabold text-sm tracking-tight text-emerald-600">AllUnitConverter</span>
                </Link>
              )}
            </div>
            
            <div className="flex-none">
              {switcherElement}
            </div>
            
            <div className="flex-1 flex justify-end">
              {/* Spacer / Right alignment */}
            </div>
          </header>

          {/* Mobile Header */}
          <header className={`desktop:hidden fixed top-0 left-0 right-0 h-14 border-b flex items-center justify-between px-2.5 z-40 transition-colors duration-300 ${
            isSleepCalc ? 'bg-slate-950/95 border-slate-900 backdrop-blur' : isSnowCalc ? 'bg-slate-900/95 border-slate-800 backdrop-blur' : 'bg-white/95 border-gray-200/85 backdrop-blur'
          }`}>
            <div className="w-10">
              {isUnitConverter ? (
                <button
                  onClick={toggleSidebar}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-600"
                  aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
                >
                  {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              ) : (
                <Link href="/" className="p-1.5 hover:bg-slate-800/50 rounded-lg text-slate-400 hover:text-emerald-500 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-emerald-500" />
                </Link>
              )}
            </div>

            <div className="flex-none">
              {switcherElement}
            </div>

            <div className="w-10" />
          </header>

          {/* Backdrop */}
          {isSidebarOpen && isUnitConverter && (
            <div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
              onClick={closeSidebar}
            />
          )}

          <div className="flex pt-14 desktop:pt-16 w-full">
            {isUnitConverter && (
              <Sidebar
                isOpen={isSidebarOpen}
                onClose={closeSidebar}
                className="w-80 fixed h-screen"
              />
            )}
            <main className={`flex-1 p-2 desktop:p-6 ${
              isUnitConverter ? 'desktop:ml-80' : 'desktop:ml-0'
            } max-w-full overflow-x-hidden min-h-[calc(100vh-4rem)]`}>
              <Breadcrumbs />
              {children}
              <div className="mt-6">
                <Footer />
              </div>
            </main>
          </div>
        </div>
      </body>
    </html>
  );
}

